import { isDevMode } from '@angular/core';
import { Routes, UrlSegment } from '@angular/router';

import { authGuard, loginGuard, noAuthGuard } from './guards/auth.guard';
import { entityRoleGuard } from './guards/entity-role.guard';
import { LoginLayoutComponent } from './layouts/login-layout/login-layout.component';
import { DashboardLayoutComponent } from './layouts/dashboard-layout/dashboard-layout.component';
import { LandingComponent } from './pages/landing/landing.component';
import { LandingHomeComponent } from './pages/landing/landing-home.component';
import { LoginComponent } from './pages/login/login.component';
import { DashboardPageComponent } from './pages/dashboard/dashboard-page.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { VerifyEmailComponent } from './pages/verify-email/verify-email.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { ChangePasswordPageComponent } from './pages/change-password/change-password-page.component';
import { ManagementListComponent } from './pages/manage/management-list.component';
import { ManagementDetailComponent } from './pages/manage/management-detail.component';
import { ManagementCreateComponent } from './pages/manage/management-create.component';
import { ManagementEditComponent } from './pages/manage/management-edit.component';

export const routes: Routes = [
  // Dev-only component showcase (no auth, not in production builds).
  ...(isDevMode()
    ? [
        {
          path: 'ui-kit',
          loadComponent: () => import('./pages/ui-kit/ui-kit.page').then((m) => m.UiKitPageComponent),
        },
      ]
    : []),
  {
    path: '',
    component: LandingComponent,
    children: [
      { path: '', component: LandingHomeComponent },
      {
        path: 'privacy-policy',
        loadComponent: () =>
          import('./pages/landing/content-pages/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
      },
      {
        path: 'terms-and-conditions',
        loadComponent: () =>
          import('./pages/landing/content-pages/terms-and-conditions.component').then((m) => m.TermsAndConditionsComponent),
      },
      {
        path: 'cookie-policy',
        loadComponent: () => import('./pages/landing/content-pages/cookie-policy.component').then((m) => m.CookiePolicyComponent),
      },
      {
        path: 'api-docs',
        loadComponent: () => import('./pages/landing/content-pages/api-docs.component').then((m) => m.ApiDocsComponent),
      },
      {
        path: 'api-docs/:section',
        loadComponent: () => import('./pages/landing/content-pages/api-docs.component').then((m) => m.ApiDocsComponent),
      },
      {
        path: 'api-docs/:section/:doc',
        loadComponent: () => import('./pages/landing/content-pages/api-docs.component').then((m) => m.ApiDocsComponent),
      },
      {
        path: 'api-docs/:section/:doc/:child',
        loadComponent: () => import('./pages/landing/content-pages/api-docs.component').then((m) => m.ApiDocsComponent),
      },
      {
        path: 'sdk-library',
        loadComponent: () => import('./pages/landing/content-pages/sdk-library.component').then((m) => m.SdkLibraryComponent),
      },
    ],
  },
  {
    path: 'login',
    canMatch: [loginGuard],
    component: LoginLayoutComponent,
    children: [{ path: '', component: LoginComponent }],
  },
  {
    path: 'forgot-password',
    canMatch: [loginGuard],
    component: LoginLayoutComponent,
    children: [{ path: '', component: ForgotPasswordComponent }],
  },
  {
    path: 'change-password',
    canMatch: [authGuard],
    component: LoginLayoutComponent,
    children: [{ path: '', component: ChangePasswordPageComponent }],
  },
  {
    path: 'verify-email',
    canMatch: [authGuard],
    component: LoginLayoutComponent,
    children: [{ path: '', component: VerifyEmailComponent }],
  },
  {
    path: 'app',
    canMatch: [authGuard],
    component: DashboardLayoutComponent,
    children: [
      { path: '', component: DashboardPageComponent },
      { path: 'settings', component: SettingsComponent },
      {
        path: 'manage/financial-entity-balances',
        canActivate: [entityRoleGuard],
        loadComponent: () =>
          import('./pages/financial-entity-balances/financial-entity-balances-page.component').then(
            (m) => m.FinancialEntityBalancesPageComponent
          ),
      },
      {
        path: 'manage/financial-entity-balances/:entityId/hub',
        canActivate: [entityRoleGuard],
        loadComponent: () =>
          import('./pages/financial-entity-balance-hub/financial-entity-balance-hub-page.component').then(
            (m) => m.FinancialEntityBalanceHubPageComponent
          ),
      },
      {
        // Financial entity hub: tabbed sections under /app/manage/entities/:id (create/edit use the generic routes).
        path: 'manage/entities/:id',
        canActivate: [entityRoleGuard],
        canMatch: [(_route: unknown, segments: UrlSegment[]) => segments[2]?.path !== 'new'],
        data: { entityKey: 'entities' },
        loadComponent: () =>
          import('./pages/entity-hub/entity-hub-shell.component').then((m) => m.EntityHubShellComponent),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'overview' },
          {
            path: 'overview',
            loadComponent: () =>
              import('./pages/entity-hub/sections/overview-section.component').then((m) => m.OverviewSectionComponent),
          },
          {
            path: 'providers',
            loadComponent: () =>
              import('./pages/entity-hub/sections/providers-section.component').then((m) => m.ProvidersSectionComponent),
          },
          {
            path: 'markets',
            loadComponent: () =>
              import('./pages/entity-hub/sections/markets-section.component').then((m) => m.MarketsSectionComponent),
          },
          {
            path: 'limits',
            loadComponent: () =>
              import('./pages/entity-hub/sections/limits-section.component').then((m) => m.LimitsSectionComponent),
          },
          {
            path: 'balances',
            loadComponent: () =>
              import('./pages/entity-hub/sections/balances-section.component').then((m) => m.BalancesSectionComponent),
          },
          {
            path: 'webhooks',
            loadComponent: () =>
              import('./pages/entity-hub/sections/webhooks-section.component').then((m) => m.WebhooksSectionComponent),
          },
          {
            path: 'configuration',
            loadComponent: () =>
              import('./pages/entity-hub/configuration/entity-configuration-shell.component').then(
                (m) => m.EntityConfigurationShellComponent,
              ),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'general' },
              {
                path: 'general',
                loadComponent: () =>
                  import('./pages/entity-hub/configuration/general-page.component').then((m) => m.GeneralPageComponent),
              },
              {
                path: 'adapter',
                loadComponent: () =>
                  import('./pages/entity-hub/configuration/adapter-page.component').then((m) => m.AdapterPageComponent),
              },
              {
                path: 'account',
                loadComponent: () =>
                  import('./pages/entity-hub/configuration/account-page.component').then((m) => m.AccountPageComponent),
              },
              {
                path: 'limits',
                loadComponent: () =>
                  import('./pages/entity-hub/configuration/limits-page.component').then((m) => m.LimitsPageComponent),
              },
              {
                path: 'routing',
                loadComponent: () =>
                  import('./pages/entity-hub/configuration/routing-page.component').then((m) => m.RoutingPageComponent),
              },
              {
                path: 'markets',
                loadComponent: () =>
                  import('./pages/entity-hub/configuration/markets-page.component').then((m) => m.MarketsPageComponent),
              },
              {
                path: 'webhooks',
                loadComponent: () =>
                  import('./pages/entity-hub/configuration/webhooks-page.component').then((m) => m.WebhooksPageComponent),
              },
            ],
          },
          {
            path: 'audit',
            loadComponent: () =>
              import('./pages/entity-hub/sections/audit-section.component').then((m) => m.AuditSectionComponent),
          },
          {
            path: 'danger',
            loadComponent: () =>
              import('./pages/entity-hub/sections/danger-section.component').then((m) => m.DangerSectionComponent),
          },
        ],
      },
      {
        // Old provider hub URL: keep bookmarks working.
        path: 'manage/providers/:id/hub',
        pathMatch: 'full',
        redirectTo: 'manage/providers/:id/overview',
      },
      {
        // API provider hub: tabbed sections under /app/manage/providers/:id.
        path: 'manage/providers/:id',
        canActivate: [entityRoleGuard],
        canMatch: [(_route: unknown, segments: UrlSegment[]) => segments[2]?.path !== 'new'],
        data: { entityKey: 'providers' },
        loadComponent: () =>
          import('./pages/provider-hub-v2/provider-hub-shell.component').then((m) => m.ProviderHubShellComponent),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'overview' },
          {
            path: 'overview',
            loadComponent: () =>
              import('./pages/provider-hub-v2/sections/overview-section.component').then((m) => m.OverviewSectionComponent),
          },
          {
            path: 'routes',
            loadComponent: () =>
              import('./pages/provider-hub-v2/sections/routes-section.component').then((m) => m.RoutesSectionComponent),
          },
          {
            path: 'balances',
            loadComponent: () =>
              import('./pages/provider-hub-v2/sections/balances-section.component').then((m) => m.BalancesSectionComponent),
          },
          {
            path: 'webhooks',
            loadComponent: () =>
              import('./pages/provider-hub-v2/sections/webhooks-section.component').then((m) => m.WebhooksSectionComponent),
          },
          {
            path: 'fees',
            loadComponent: () =>
              import('./pages/provider-hub-v2/sections/fees-section.component').then((m) => m.FeesSectionComponent),
          },
          {
            path: 'configuration',
            loadComponent: () =>
              import('./pages/provider-hub-v2/configuration/provider-configuration-shell.component').then(
                (m) => m.ProviderConfigurationShellComponent
              ),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'general' },
              {
                path: 'general',
                loadComponent: () =>
                  import('./pages/provider-hub-v2/configuration/general-page.component').then((m) => m.GeneralPageComponent),
              },
              {
                path: 'adapter',
                loadComponent: () =>
                  import('./pages/provider-hub-v2/configuration/adapter-page.component').then((m) => m.AdapterPageComponent),
              },
              {
                path: 'secrets',
                loadComponent: () =>
                  import('./pages/provider-hub-v2/configuration/secrets-page.component').then((m) => m.SecretsPageComponent),
              },
              {
                path: 'webhooks',
                loadComponent: () =>
                  import('./pages/provider-hub-v2/configuration/webhooks-page.component').then((m) => m.WebhooksPageComponent),
              },
              {
                path: 'health',
                loadComponent: () =>
                  import('./pages/provider-hub-v2/configuration/health-page.component').then((m) => m.HealthPageComponent),
              },
              {
                path: 'balance',
                loadComponent: () =>
                  import('./pages/provider-hub-v2/configuration/balance-page.component').then((m) => m.BalancePageComponent),
              },
            ],
          },
          {
            path: 'audit',
            loadComponent: () =>
              import('./pages/provider-hub-v2/sections/audit-section.component').then((m) => m.AuditSectionComponent),
          },
          {
            path: 'danger',
            loadComponent: () =>
              import('./pages/provider-hub-v2/sections/danger-section.component').then((m) => m.DangerSectionComponent),
          },
        ],
      },
      {
        path: 'manage/provider-balances/:apiProviderId/hub',
        canActivate: [entityRoleGuard],
        loadComponent: () =>
          import('./pages/provider-balance-hub/provider-balance-hub-page.component').then(
            (m) => m.ProviderBalanceHubPageComponent
          ),
      },
      {
        path: 'fees-hub',
        loadComponent: () =>
          import('./pages/fees-hub/fees-hub-page.component').then((m) => m.FeesHubPageComponent),
      },
      {
        // Merchant hub: tabbed sections under /app/manage/merchants/:id (the generic detail route below handles the rest).
        path: 'manage/merchants/:id',
        canActivate: [entityRoleGuard],
        canMatch: [(_route: unknown, segments: UrlSegment[]) => segments[2]?.path !== 'new'],
        data: { entityKey: 'merchants' },
        loadComponent: () =>
          import('./pages/merchant-hub/merchant-hub-shell.component').then((m) => m.MerchantHubShellComponent),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'overview' },
          {
            path: 'overview',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/overview-section.component').then((m) => m.OverviewSectionComponent),
          },
          { path: 'transactions', pathMatch: 'full', redirectTo: 'transactions/deposits' },
          {
            path: 'transactions/:kind',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/transactions-section.component').then(
                (m) => m.TransactionsSectionComponent
              ),
          },
          {
            path: 'operations',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/operations-section.component').then(
                (m) => m.OperationsSectionComponent
              ),
          },
          {
            path: 'settlements',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/settlements-section.component').then(
                (m) => m.SettlementsSectionComponent
              ),
          },
          {
            path: 'configuration',
            loadComponent: () =>
              import('./pages/merchant-hub/configuration/merchant-configuration-shell.component').then(
                (m) => m.MerchantConfigurationShellComponent
              ),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'checkout' },
              {
                path: 'checkout',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/checkout-page.component').then(
                    (m) => m.CheckoutPageComponent
                  ),
              },
              {
                path: 'payments',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/payments-page.component').then(
                    (m) => m.PaymentsPageComponent
                  ),
              },
              {
                path: 'card',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/card-page.component').then(
                    (m) => m.CardPageComponent
                  ),
              },
              {
                path: 'profile',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/profile-page.component').then(
                    (m) => m.ProfilePageComponent
                  ),
              },
              {
                path: 'portal',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/portal-page.component').then(
                    (m) => m.PortalPageComponent
                  ),
              },
              {
                path: 'countries',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/countries-page.component').then(
                    (m) => m.CountriesPageComponent
                  ),
              },
              {
                path: 'wallets',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/wallets-page.component').then(
                    (m) => m.WalletsPageComponent
                  ),
              },
              {
                path: 'providers',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/providers-page.component').then(
                    (m) => m.ProvidersPageComponent
                  ),
              },
              {
                path: 'operation-types',
                loadComponent: () =>
                  import('./pages/merchant-hub/configuration/operation-types-page.component').then(
                    (m) => m.OperationTypesPageComponent
                  ),
              },
            ],
          },
          {
            path: 'fees',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/fees-section.component').then((m) => m.FeesSectionComponent),
          },
          {
            path: 'developer',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/developer-section.component').then(
                (m) => m.DeveloperSectionComponent
              ),
          },
          {
            path: 'members',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/members-section.component').then((m) => m.MembersSectionComponent),
          },
          {
            path: 'security',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/security-section.component').then(
                (m) => m.SecuritySectionComponent
              ),
          },
          {
            path: 'danger',
            loadComponent: () =>
              import('./pages/merchant-hub/sections/danger-section.component').then((m) => m.DangerSectionComponent),
          },
        ],
      },
      {
        path: 'manage/message-queues/list',
        canActivate: [entityRoleGuard],
        data: { entityKey: 'message-queues' },
        loadComponent: () =>
          import('./pages/message-queues-dashboard/message-queues-list-page.component').then(
            (m) => m.MessageQueuesListPageComponent
          ),
      },
      { path: 'manage/:entityKey', component: ManagementListComponent, canActivate: [entityRoleGuard] },
      { path: 'manage/:entityKey/new', component: ManagementCreateComponent, canActivate: [entityRoleGuard] },
      { path: 'manage/:entityKey/:id', component: ManagementDetailComponent, canActivate: [entityRoleGuard] },
      { path: 'manage/:entityKey/:id/edit', component: ManagementEditComponent, canActivate: [entityRoleGuard] },
    ],
  },
  { path: '**', redirectTo: '/app' },
];
